import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faChevronUp, faChevronDown, faTimes } from '@fortawesome/free-solid-svg-icons';

/**
 * Ultra-compact search bar for request/response panels.
 * Toggled via Ctrl+F. Single row, minimal height.
 */
const HttpSearchBar = ({
  isDarkMode,
  onSearchChange,
  totalMatches = 0,
  activeMatchIndex = -1,
  onNext,
  onPrev,
  onClose,
  visible
}) => {
  const [query, setQuery] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [useRegex, setUseRegex] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (visible && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [visible]);

  // Debounce the search so it runs once the user pauses typing (not on every
  // keystroke). Also require >=2 chars for plain-text searches: a single letter
  // matches thousands of times in a large body and is never a useful search.
  useEffect(() => {
    const effective = (!useRegex && query.length === 1) ? '' : query;
    const id = setTimeout(() => {
      onSearchChange(effective, { caseSensitive, useRegex });
    }, 300);
    return () => clearTimeout(id);
  }, [query, caseSensitive, useRegex, onSearchChange]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); e.shiftKey ? onPrev() : onNext(); }
    if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  if (!visible) return null;

  const btnBase = `px-1 py-0 text-[9px] rounded font-mono font-bold leading-none`;

  return (
    <div className={`flex items-center gap-1.5 px-2 py-1 border-b ${
      isDarkMode ? 'border-gray-700 bg-gray-850' : 'border-gray-200 bg-gray-50'
    }`} style={{ minHeight: 28, maxHeight: 28 }}>
      <FontAwesomeIcon icon={faSearch} className={`text-[9px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
      <input ref={inputRef} type="text" value={query}
        onChange={(e) => setQuery(e.target.value)} onKeyDown={handleKeyDown}
        placeholder="Search..."
        className={`text-xs px-1.5 py-0.5 rounded border outline-none w-48 ${
          isDarkMode
            ? 'bg-gray-900 border-gray-700 text-gray-200 placeholder-gray-600 focus:border-teal-600'
            : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-teal-500'
        }`}
        style={{ height: 20 }}
      />
      <button onClick={() => setCaseSensitive(!caseSensitive)}
        className={`${btnBase} ${caseSensitive ? (isDarkMode ? 'bg-teal-900/50 text-teal-300' : 'bg-teal-100 text-teal-700') : (isDarkMode ? 'text-gray-600 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600')}`}
        title="Case sensitive">Aa</button>
      <button onClick={() => setUseRegex(!useRegex)}
        className={`${btnBase} ${useRegex ? (isDarkMode ? 'bg-teal-900/50 text-teal-300' : 'bg-teal-100 text-teal-700') : (isDarkMode ? 'text-gray-600 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600')}`}
        title="Regex">.*</button>

      <span className={`text-[10px] w-14 text-center tabular-nums ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        {query ? (totalMatches > 0 ? `${activeMatchIndex + 1}/${totalMatches}` : '0/0') : ''}
      </span>

      <button onClick={onPrev} disabled={totalMatches === 0} title="Shift+Enter"
        className={`p-0.5 rounded leading-none ${totalMatches > 0 ? (isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800') : (isDarkMode ? 'text-gray-700' : 'text-gray-300')}`}>
        <FontAwesomeIcon icon={faChevronUp} className="text-[9px]" />
      </button>
      <button onClick={onNext} disabled={totalMatches === 0} title="Enter"
        className={`p-0.5 rounded leading-none ${totalMatches > 0 ? (isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800') : (isDarkMode ? 'text-gray-700' : 'text-gray-300')}`}>
        <FontAwesomeIcon icon={faChevronDown} className="text-[9px]" />
      </button>
      <button onClick={onClose} title="Esc"
        className={`p-0.5 rounded leading-none ${isDarkMode ? 'text-gray-600 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>
        <FontAwesomeIcon icon={faTimes} className="text-[9px]" />
      </button>
    </div>
  );
};

export default HttpSearchBar;
